import { useDialerAuth } from '@/contexts/DialerAuthContext';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Send, Copy, Loader2, Mail, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/api/api-client';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { applyTemplate } from '@/utils/templateUtils';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';

const EMAIL_TYPES = [
  { value: 'confirmation_rdv', labelKey: 'confirmation', icon: '📅' },
  { value: 'recap_conversation', labelKey: 'recap', icon: '💬' },
  { value: 'envoi_information', labelKey: 'info', icon: '📄' },
  { value: 'remerciement', labelKey: 'thanks', icon: '🙏' }
];

interface CallShape {
  id?: string;
  prospect_name?: string | null;
  prospect_phone?: string | null;
  prospect_company?: string | null;
  prospect_email?: string | null;
  call_notes?: string | null;
  call_outcome?: string | null;
  transcription?: unknown;
  duration_seconds?: number | null;
}

interface GeneratedEmail {
  subject: string;
  body: string;
  to: string;
}

interface ApiShape {
  functions: {
    invoke: (
      name: string,
      params: Record<string, unknown>,
    ) => Promise<{ data?: { subject?: string; body?: string; to?: string }; error?: string }>;
  };
}

interface AIEmailGeneratorProps {
  call?: CallShape | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function AIEmailGenerator({ call, isOpen, onClose }: AIEmailGeneratorProps) {
  const { t } = useTranslation(['admin', 'dialer']);
  // From the session, not the localStorage copy, which can be stale.
  const { callerData: sessionCaller } = useDialerAuth();
  const [emailType, setEmailType] = useState('confirmation_rdv');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState<GeneratedEmail | null>(null);
  const [editableEmail, setEditableEmail] = useState({
    to: '',
    subject: '',
    body: ''
  });

  useEffect(() => {
    if (isOpen && call) {
      setEmailType('confirmation_rdv');
      setGeneratedEmail(null);
      setEditableEmail({
        to: call.prospect_email || '',
        subject: '',
        body: ''
      });
    }
  }, [isOpen, call]);

  const handleGenerate = async () => {
    if (!call) return;

    setIsGenerating(true);
    try {
      const context = {
        prospectName: call.prospect_name,
        prospectPhone: call.prospect_phone,
        prospectCompany: call.prospect_company,
        prospectEmail: call.prospect_email,
        callNotes: call.call_notes,
        callOutcome: call.call_outcome,
        transcription: call.transcription,
        durationSeconds: call.duration_seconds,
        emailType
      };

      const response = await (api as unknown as ApiShape).functions.invoke('generateEmail', {
        callId: call.id,
        emailType,
        context
      });

      if (response.data) {
        const { subject, body, to } = response.data;
        const callerData = sessionCaller || {};
        const resolvedSubject = applyTemplate(subject || '', call as never, callerData);
        const resolvedBody = applyTemplate(body || '', call as never, callerData);
        setGeneratedEmail({ subject: resolvedSubject, body: resolvedBody, to: to || call.prospect_email || '' });
        setEditableEmail({
          to: to || call.prospect_email || '',
          subject: resolvedSubject,
          body: resolvedBody
        });
        toast.success(t('dialer:aiEmail.toastGenerated'), {
          description: t('dialer:aiEmail.toastGeneratedDesc')
        });
      } else {
        throw new Error(response.error || t('dialer:aiEmail.generationError'));
      }
    } catch (error) {
      console.error('Error generating email:', error);
      toast.error(t('dialer:aiEmail.generationError'), {
        description: (error as Error).message || t('dialer:aiEmail.generationErrorDesc')
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!editableEmail.to || !editableEmail.subject || !editableEmail.body) {
      toast.error(t('dialer:aiEmail.requiredFields'), {
        description: t('dialer:aiEmail.requiredFieldsDesc')
      });
      return;
    }

    if (!validateEmail(editableEmail.to)) {
      toast.error(t('dialer:aiEmail.invalidEmail'), {
        description: t('dialer:aiEmail.invalidEmailDesc')
      });
      return;
    }

    setIsSending(true);
    try {
      await (api as unknown as ApiShape).functions.invoke('sendEmail', {
        to: editableEmail.to,
        subject: editableEmail.subject,
        body: editableEmail.body,
        callId: call?.id
      });

      toast.success(t('dialer:aiEmail.toastSent'), {
        description: t('dialer:aiEmail.toastSentDesc', { to: editableEmail.to })
      });
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(t('dialer:aiEmail.sendError'), {
        description: (error as Error).message || t('dialer:aiEmail.sendErrorDesc')
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('dialer:aiEmail.copied'), {
        description: t('dialer:aiEmail.copiedDesc', { label })
      });
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error(t('dialer:aiEmail.copyError'), {
        description: t('dialer:aiEmail.copyErrorDesc')
      });
    }
  };

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  if (!call) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Mail className="w-5 h-5 text-[var(--vf-accent-text)]" />
              {t('admin:components.aiEmailGenerator.title')}
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Call Info Summary */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-900">{call.prospect_name}</div>
                <div className="text-sm text-slate-600">
                  {call.prospect_company && <span>{call.prospect_company} • </span>}
                  <span>{call.prospect_phone}</span>
                </div>
              </div>
              {call.call_outcome && (
                <Badge variant="outline" className="capitalize">
                  {outcomeLabel(call.call_outcome, t)}
                </Badge>
              )}
            </div>
            {call.prospect_email && (
              <div className="text-sm text-slate-600">
                {t('dialer:aiEmail.emailLine', { email: call.prospect_email })}
              </div>
            )}
          </div>

          {/* Email Type Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              {t('dialer:aiEmail.emailType')}
            </label>
            <Select value={emailType} onValueChange={setEmailType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EMAIL_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <span className="flex items-center gap-2">
                      <span>{type.icon}</span>
                      <span>{t(`dialer:aiEmail.types.${type.labelKey}`)}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Generate Button */}
          {!generatedEmail && (
            <Button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="w-full"
              size="lg"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('dialer:aiEmail.generating')}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('dialer:aiEmail.generate')}
                </>
              )}
            </Button>
          )}

          {/* Generated Email Form */}
          <AnimatePresence>
            {generatedEmail && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-4"
              >
                {/* To Field */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    {t('dialer:aiEmail.recipient')}
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={editableEmail.to}
                      onChange={(e) => setEditableEmail({ ...editableEmail, to: e.target.value })}
                      placeholder={t('dialer:aiEmail.recipientPlaceholder')}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleCopy(editableEmail.to, t('dialer:aiEmail.recipient'))}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Subject Field */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    {t('dialer:aiEmail.subject')}
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={editableEmail.subject}
                      onChange={(e) => setEditableEmail({ ...editableEmail, subject: e.target.value })}
                      placeholder={t('dialer:aiEmail.subjectPlaceholder')}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleCopy(editableEmail.subject, t('dialer:aiEmail.subject'))}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Body Field */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">
                      {t('dialer:aiEmail.body')}
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(editableEmail.body, t('dialer:aiEmail.body'))}
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      {t('dialer:aiEmail.copy')}
                    </Button>
                  </div>
                  <Textarea
                    value={editableEmail.body}
                    onChange={(e) => setEditableEmail({ ...editableEmail, body: e.target.value })}
                    placeholder={t('dialer:aiEmail.bodyPlaceholder')}
                    rows={12}
                    className="font-mono text-sm resize-y"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="flex-1"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('dialer:aiEmail.regenerating')}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        {t('dialer:aiEmail.regenerate')}
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleSend}
                    disabled={isSending || !editableEmail.to || !editableEmail.subject || !editableEmail.body}
                    className="flex-1"
                  >
                    {isSending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('dialer:aiEmail.sending')}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {t('dialer:aiEmail.send')}
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Help Text */}
          {!generatedEmail && (
            <div className="text-xs text-slate-500 text-center">
              {t('dialer:aiEmail.help')}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
